import bus, { Prerender } from "@/core/api/bus"
import { sessionStore } from "@/core/store"
import context from "@/core/context"
import { CustomerAffinityResponse, CustomerAffinityResponseItem } from "@/types"
import ev1state from "@/core/ev1/state"
import { PersonalizationBoost, SearchSessionParams } from "./types"
import { searchOrCategoriesEnabled } from "./utils"
import { getForcedSegments } from "@/core/segments"

type SessionParamsData = Pick<Prerender, "affinityScores" | "segments">

const cacheKey = "nosto:search:sessionParams:v2"
const sessionParamsFn = ev1state(transformToSearchSessionParams)

export function initSessionParams() {
  if (searchOrCategoriesEnabled()) {
    // registering caching side effect on prerender
    bus.on("prerender", ({ affinityScores, segments }) => {
      const data = { affinityScores, segments }
      sessionStore.setAsJson(cacheKey, transformToSearchSessionParams(data))
    })
  }
}

export default async function getSearchSessionParams() {
  let sessionParams = sessionStore.getAsJson<SearchSessionParams>(cacheKey)
  if (!sessionParams && !context.mode.isBot()) {
    // cached version not available, use ev1state helpers to fetch data
    sessionParams = await sessionParamsFn()
  }
  return appendForcedSegments(sessionParams)
}

export function scoreToNumber(score: number | string) {
  const asNumber = parseFloat(score.toString())
  return isNaN(asNumber) ? 0 : asNumber
}

function getFieldBoosts(field: string, fieldAffinities: CustomerAffinityResponseItem[]) {
  return fieldAffinities.reduce<PersonalizationBoost[]>((acc, affinity) => {
    const affinityScore = scoreToNumber(affinity.score)
    if (!affinityScore) {
      return acc
    }
    const existingBoostIndex = acc.findIndex(v => v.field === field && v.weight === affinityScore)
    if (existingBoostIndex > -1) {
      return acc.map((existingValue, index) =>
        index === existingBoostIndex
          ? {
              ...existingValue,
              value: [...existingValue.value, affinity.name.toLowerCase()]
            }
          : existingValue
      )
    }
    return [
      ...acc,
      {
        field,
        weight: affinityScore,
        value: [affinity.name.toLowerCase()]
      }
    ]
  }, [])
}

function transformToPersonalizationBoost(affinities: CustomerAffinityResponse): PersonalizationBoost[] {
  return [
    ...getFieldBoosts("affinities.categories", affinities.top_categories || []),
    ...getFieldBoosts("affinities.brand", affinities.top_brands || []),
    ...getFieldBoosts("affinities.productType", affinities.top_product_types || []),
    ...Object.keys(affinities.top_skus || {})
      .sort()
      .flatMap(skuField => getFieldBoosts(`affinities.${skuField}`, affinities.top_skus![skuField] || []))
  ]
}

function transformToSearchSessionParams({ affinityScores, segments }: SessionParamsData) {
  const personalizationBoost = affinityScores ? transformToPersonalizationBoost(affinityScores || {}) : []
  const segmentsData = segments?.active_segments?.map(segment => segment.id) || []

  return {
    segments: segmentsData,
    products: {
      personalizationBoost
    }
  }
}

function appendForcedSegments(data: SearchSessionParams = {}) {
  const forcedSegments = getForcedSegments()
  const segments = data.segments || []

  if (segments.length > 0 || forcedSegments.length > 0) {
    return {
      ...data,
      segments: [...segments, ...forcedSegments.filter(fs => !segments.includes(fs))]
    }
  }

  return data
}
